import { Check } from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import BookmarkBorderRoundedIcon from "@mui/icons-material/BookmarkBorderRounded";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import ClearIcon from "@mui/icons-material/Clear";
import ExitToAppRoundedIcon from "@mui/icons-material/ExitToAppRounded";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import ReplayRoundedIcon from "@mui/icons-material/ReplayRounded";
import SettingsIcon from "@mui/icons-material/Settings";
import StorageRoundedIcon from "@mui/icons-material/StorageRounded";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import TimerRoundedIcon from "@mui/icons-material/TimerRounded";
import VpnKeyRoundedIcon from "@mui/icons-material/VpnKeyRounded";
import { Checkbox, Divider, FormHelperText, Grid } from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import Avatar from "@mui/material/Avatar";
import Badge from "@mui/material/Badge";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import ListItemIcon from "@mui/material/ListItemIcon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { makeStyles } from "@mui/styles";
import React, { useEffect, useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import type {
	Datastore,
	DatastoreOverviewEntry,
} from "../../../types/datastore";
import type { PopupEntry, PopupMode } from "../../../types/popup-ui";
import type { ParsedUrl } from "../../../types/utilities";
import type { SecretReference } from "../../../types/vault";

import action from "../../actions/bound-action-creators";
import DarkBox from "../../components/dark-box";
import DialogChangeAccount from "../../components/dialogs/change-account";
import DialogUnlockOfflineCache from "../../components/dialogs/unlock-offline-cache";
import EntryIcon from "../../components/entry-icon";
import GatewayLaunchButton from "../../components/gateway-launch-button";
import GeneratorStrength from "../../components/generator-strength";
import ContentCopy from "../../components/icons/ContentCopy";
import TextFieldColored from "../../components/text-field/colored";
import { GeneratorTypeSelect } from "../../components/generator-select";
import PassphraseGenerator from "../../components/passphrase-generator-lazy";
import accountService from "../../services/account";
import browserClient from "../../services/browser-client";
import datastoreService from "../../services/datastore";
import datastorePassword from "../../services/datastore-password";
import { passwordEntropy } from "../../services/password-entropy";
import helper from "../../services/helper";
import offlineCacheService from "../../services/offline-cache";
import offlineCache from "../../services/offline-cache";
import secretService from "../../services/secret";
import storage from "../../services/storage";
import { getStore } from "../../services/store";
import user from "../../services/user";
import widgetService from "../../services/widget";
import CreateDatastoresDialog from "../other/create-datastores-dialog";

const useStyles = makeStyles((theme) => ({
	root: {
		color: theme.palette.lightGreyText.main,
	},
	popupContainer: {
		width: "550px",
		minWidth: "550px",
		maxWidth: "550px",
		boxSizing: "border-box",
	},
	textField: {
		width: "100%",
		"& .MuiInputBase-root": {
			color: theme.palette.lightGreyText.main,
		},
		"& .MuiInputAdornment-root .MuiTypography-colorTextSecondary": {
			color: theme.palette.greyText.main,
		},
		"& MuiFormControl-root": {
			color: theme.palette.lightGreyText.main,
		},
		"& label": {
			color: theme.palette.lightGreyText.main,
		},
		"& .MuiInput-underline:after": {
			borderBottomColor: "green",
		},
		"& .MuiOutlinedInput-root": {
			"& fieldset": {
				borderColor: theme.palette.greyText.main,
			},
		},
	},
	divider: {
		background: theme.palette.greyText.main,
		marginTop: "20px",
		marginBottom: "20px",
	},
	button: {
		color: theme.palette.lightGreyText.main,
		width: "100%",
		transition: "none",
		"& span": {
			justifyContent: "left",
		},
		"&:hover": {
			backgroundColor: theme.palette.lightBackground.main,
			color: theme.palette.blueBackground.main,
		},
	},
	entriesContainer: {
		height: "300px",
		overflow: "hidden",
		overflowY: "auto",
	},
	navigation: {
		listStyleType: "none",
		padding: 0,
		margin: 0,
	},
	noEntriesMessage: {
		height: "300px",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
	},
	navigationItemLi: {
		position: "relative",
		marginTop: "1px",
		borderRadius: "4px",
		"& p": {
			color: theme.palette.lightGreyText.main,
		},
		"&:hover": {
			backgroundColor: theme.palette.lightBackground.main,
			color: theme.palette.blueBackground.main,
		},
		"&:hover a": {
			color: theme.palette.blueBackground.main,
		},
		"&:hover p": {
			color: theme.palette.blueBackground.main,
		},
		"& button span": {
			color: theme.palette.lightGreyText.main,
		},
		"&:hover button span": {
			color: theme.palette.blueBackground.main,
		},
		"& button svg": {
			color: theme.palette.lightGreyText.main,
		},
		"&:hover button svg": {
			color: theme.palette.blueBackground.main,
		},
	},
	navigationItemA: {
		textDecoration: "none",
		display: "inline-block",
		padding: "10px 100px 10px 10px",
		width: "100%",
	},
	navigationItemButtonGroup: {
		position: "absolute",
		right: "0px",
		top: "3px",
	},
	listItemIcon: {
		minWidth: theme.spacing(4),
	},
	icon: {
		fontSize: "18px",
	},
	checked: {
		color: theme.palette.checked.main,
	},
	checkedIcon: {
		width: "20px",
		height: "20px",
		border: `1px solid ${theme.palette.greyText.main}`,
		borderRadius: "3px",
	},
	uncheckedIcon: {
		width: "0px",
		height: "0px",
		padding: "9px",
		border: `1px solid ${theme.palette.greyText.main}`,
		borderRadius: "3px",
	},
	widePopper: {
		width: "max-content",
	},
	menuButton: {
		textTransform: "none",
		padding: "4px",
	},
	menuButtonSlim: {
		paddingTop: "4px",
		paddingBottom: "4px",
		paddingLeft: "0px",
		paddingRight: "0px",
		minWidth: "10px",
	},
	overlayIcon: {
		position: "absolute",
		width: "0.6em",
		height: "0.6em",
		bottom: 4,
		right: 4,
		backgroundColor: theme.palette.background.paper,
		borderRadius: "50%",
		color: theme.palette.secondary.main,
		border: `1px solid ${theme.palette.background.paper}`,
	},
	overlayedIcon: {
		width: 25,
		height: 25,
		backgroundColor: "#999",
		marginLeft: "0px",
		marginRight: "0px",
		color: "white",
	},
	description: {
		fontSize: "12px",
	},
	regularButtonText: {
		color: theme.palette.lightGreyText.main,
	},
}));

interface PopupItemProps {
	editItem: (item: Datastore) => void;
	onItemClick: (item: Datastore) => void;
	item: PopupEntry;
}

const PopupItem = (props: PopupItemProps) => {
	const classes = useStyles();
	const { t } = useTranslation();
	const { editItem, onItemClick, item } = props;
	const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);

	const openMenu = (event: React.MouseEvent<HTMLElement>) => {
		event.preventDefault();
		event.stopPropagation();
		setAnchorEl(event.currentTarget);
	};

	const handleClose = (event: React.SyntheticEvent) => {
		event.preventDefault();
		event.stopPropagation();
		setAnchorEl(null);
	};

	// The copy controls below are selected by the entry's secret type.
	const onCopyUsername = (event: React.MouseEvent) => {
		event.preventDefault();
		event.stopPropagation();
		secretService.copyUsername(item.content as SecretReference);
	};

	const onCopyPassword = (event: React.MouseEvent) => {
		event.preventDefault();
		event.stopPropagation();
		secretService.copyPassword(item.content as SecretReference);
	};

	const onCopyTotpToken = (event: React.MouseEvent) => {
		event.preventDefault();
		event.stopPropagation();
		secretService.copyTotpToken(item.content as SecretReference);
	};

	const onCopyNoteContent = (event: React.MouseEvent) => {
		secretService.copyNoteContent(item.content as SecretReference);
		handleClose(event);
	};

	const onCopyCreditCardNumber = (event: React.MouseEvent) => {
		secretService.copyCreditCardNumber(item.content as SecretReference);
		handleClose(event);
	};

	const onCopyCreditCardName = (event: React.MouseEvent) => {
		secretService.copyCreditCardName(item.content as SecretReference);
		handleClose(event);
	};

	const onCopyCreditCardExpiryDate = (event: React.MouseEvent) => {
		secretService.copyCreditCardExpiryDate(item.content as SecretReference);
		handleClose(event);
	};

	const onCopyCreditCardCvc = (event: React.MouseEvent) => {
		secretService.copyCreditCardCvc(item.content as SecretReference);
		handleClose(event);
	};

	const onCopyCreditCardPin = (event: React.MouseEvent) => {
		secretService.copyCreditCardPin(item.content as SecretReference);
		handleClose(event);
	};

	const onCopyURL = (event: React.MouseEvent) => {
		secretService.copyUrl(item.content as SecretReference);
		handleClose(event);
	};

	const onEditItem = (event: React.MouseEvent) => {
		handleClose(event);
		editItem(item.content);
	};

	const title = item.content.name;
	let description = "";

	if (
		Object.hasOwn(item.content, "description") &&
		item.content.description &&
		(!Object.hasOwn(item.content, "name") ||
			!(item.content.name || "")
				.toLowerCase()
				.includes(item.content.description.toLowerCase()))
	) {
		description = item.content.description;
	}

	return (
		<li className={classes.navigationItemLi}>
			<a href="#" className={classes.navigationItemA} onClick={onEditItem}>
				<EntryIcon
					key={item.content.secret_id || item.content.file_id}
					entry={item.content}
				/>{" "}
				{title}
				{!!description && (
					<span className={classes.description}>&nbsp;({description})</span>
				)}
				<br />
				<Tooltip
					disableInteractive
					title={item.path}
					placement="top"
					PopperProps={{
						disablePortal: true,
					}}
				>
					<FormHelperText
						style={{ overflow: "hidden", textOverflow: "ellipsis" }}
					>
						{item.path}
					</FormHelperText>
				</Tooltip>
			</a>
			<ButtonGroup
				variant="text"
				aria-label="outlined button group"
				className={classes.navigationItemButtonGroup}
			>
				<GatewayLaunchButton
					className={classes.regularButtonText}
					item={item.content}
				/>
				{["bookmark", "website_password", "elster_certificate"].indexOf(
					item.content.type!,
				) !== -1 && (
					<Button
						aria-label="open"
						onClick={() => {
							onItemClick(item.content);
						}}
					>
						<OpenInNewIcon
							fontSize="small"
							className={classes.regularButtonText}
						/>
					</Button>
				)}
				{[
					"application_password",
					"website_password",
					"ssh_connection",
					"rdp_connection",
					"vnc_connection",
				].indexOf(item.content.type!) !== -1 && (
					<>
						<Tooltip
							disableInteractive
							title={t("COPY_USERNAME")}
							placement="left"
							PopperProps={{
								disablePortal: true,
							}}
							classes={{
								tooltip: classes.widePopper,
							}}
						>
							<Button aria-label="copy username" onClick={onCopyUsername}>
								<PersonRoundedIcon fontSize="small" />
							</Button>
						</Tooltip>
						<Tooltip
							disableInteractive
							title={t("COPY_PASSWORD")}
							placement="left"
							PopperProps={{
								disablePortal: true,
							}}
							classes={{
								tooltip: classes.widePopper,
							}}
						>
							<Button aria-label="copy password" onClick={onCopyPassword}>
								<LockRoundedIcon fontSize="small" />
							</Button>
						</Tooltip>
						{item.content.type === "website_password" && (
							<Tooltip
								disableInteractive
								title={t("COPY_TOTP_TOKEN")}
								placement="left"
								PopperProps={{
									disablePortal: true,
								}}
								classes={{
									tooltip: classes.widePopper,
								}}
							>
								<Button aria-label="copy totp token" onClick={onCopyTotpToken}>
									<TimerRoundedIcon fontSize="small" />
								</Button>
							</Tooltip>
						)}
					</>
				)}
				{["credit_card"].indexOf(item.content.type!) !== -1 && (
					<Button aria-label="settings" onClick={openMenu}>
						<ContentCopy
							fontSize="small"
							className={classes.regularButtonText}
						/>
					</Button>
				)}
				{["totp"].indexOf(item.content.type!) !== -1 && (
					<Tooltip
						disableInteractive
						title={t("COPY_TOTP_TOKEN")}
						placement="left"
						PopperProps={{
							disablePortal: true,
						}}
						classes={{
							tooltip: classes.widePopper,
						}}
					>
						<Button aria-label="settings" onClick={onCopyTotpToken}>
							<ContentCopy
								fontSize="small"
								className={classes.regularButtonText}
							/>
						</Button>
					</Tooltip>
				)}
				{["note"].indexOf(item.content.type!) !== -1 && (
					<Tooltip
						disableInteractive
						title={t("COPY_NOTE_CONTENT")}
						placement="left"
						PopperProps={{
							disablePortal: true,
						}}
						classes={{
							tooltip: classes.widePopper,
						}}
					>
						<Button aria-label="settings" onClick={onCopyNoteContent}>
							<ContentCopy
								fontSize="small"
								className={classes.regularButtonText}
							/>
						</Button>
					</Tooltip>
				)}
				{["bookmark"].indexOf(item.content.type!) !== -1 && (
					<Tooltip
						disableInteractive
						title={t("COPY_URL")}
						placement="left"
						PopperProps={{
							disablePortal: true,
						}}
						classes={{
							tooltip: classes.widePopper,
						}}
					>
						<Button aria-label="settings" onClick={onCopyURL}>
							<ContentCopy
								fontSize="small"
								className={classes.regularButtonText}
							/>
						</Button>
					</Tooltip>
				)}
			</ButtonGroup>
			{["credit_card"].indexOf(item.content.type!) !== -1 && (
				<Menu
					id="simple-menu"
					anchorEl={anchorEl}
					keepMounted
					open={Boolean(anchorEl)}
					onClose={(event) => handleClose(event as React.SyntheticEvent)}
				>
					{["credit_card"].indexOf(item.content.type!) !== -1 && [
						<MenuItem key="copy-cc-number" onClick={onCopyCreditCardNumber}>
							<ListItemIcon className={classes.listItemIcon}>
								<ContentCopy className={classes.icon} fontSize="small" />
							</ListItemIcon>
							<Typography variant="body2" noWrap>
								{t("COPY_CREDIT_CARD_NUMBER")}
							</Typography>
						</MenuItem>,
						<MenuItem key="copy-cc-name" onClick={onCopyCreditCardName}>
							<ListItemIcon className={classes.listItemIcon}>
								<ContentCopy className={classes.icon} fontSize="small" />
							</ListItemIcon>
							<Typography variant="body2" noWrap>
								{t("COPY_CREDIT_CARD_NAME")}
							</Typography>
						</MenuItem>,
						<MenuItem key="copy-cc-expiry" onClick={onCopyCreditCardExpiryDate}>
							<ListItemIcon className={classes.listItemIcon}>
								<ContentCopy className={classes.icon} fontSize="small" />
							</ListItemIcon>
							<Typography variant="body2" noWrap>
								{t("COPY_CREDIT_CARD_EXPIRATION_DATE")}
							</Typography>
						</MenuItem>,
						<MenuItem key="copy-cc-cvc" onClick={onCopyCreditCardCvc}>
							<ListItemIcon className={classes.listItemIcon}>
								<ContentCopy className={classes.icon} fontSize="small" />
							</ListItemIcon>
							<Typography variant="body2" noWrap>
								{t("COPY_CREDIT_CARD_CVC")}
							</Typography>
						</MenuItem>,
						<MenuItem key="copy-cc-pin" onClick={onCopyCreditCardPin}>
							<ListItemIcon className={classes.listItemIcon}>
								<ContentCopy className={classes.icon} fontSize="small" />
							</ListItemIcon>
							<Typography variant="body2" noWrap>
								{t("COPY_CREDIT_CARD_PIN")}
							</Typography>
						</MenuItem>,
					]}
				</Menu>
			)}
		</li>
	);
};

const PopupView = (
	props: Omit<
		React.ComponentProps<typeof CreateDatastoresDialog>,
		"open" | "onClose"
	>,
) => {
	const classes = useStyles();
	const { t } = useTranslation();
	const passwordDatastore = useSelector((state) =>
		state.user.userDatastoreOverview?.datastores?.find(
			(datastore) => datastore.type === "password" && datastore.is_default,
		),
	);
	const isLoggedIn = useSelector((state) => state.user.isLoggedIn);
	const hasTwoFactor = useSelector((state) => state.user.hasTwoFactor);
	const [unlockOfflineCache, setUnlockOfflineCache] = React.useState(false);
	const [view, setView] = React.useState<PopupMode>("default");
	const [includeLettersLowercase, setIncludeLettersLowercase] = useState(true);
	const [includeLettersUppercase, setIncludeLettersUppercase] = useState(true);
	const [includeNumbers, setIncludeNumbers] = useState(true);
	const [includeSpecialChars, setIncludeSpecialChars] = useState(true);
	const settingsDatastore = useSelector((state) => state.settingsDatastore);
	const [generator, setGenerator] = useState<"password" | "passphrase">(
		"password",
	);
	const [passwordLength, setPasswordLength] = useState<number | string>(
		settingsDatastore.passwordLength,
	);
	const [passwordLettersUppercase, setPasswordLettersUppercase] = useState(
		settingsDatastore.passwordLettersUppercase,
	);
	const [passwordLettersLowercase, setPasswordLettersLowercase] = useState(
		settingsDatastore.passwordLettersLowercase,
	);
	const [passwordNumbers, setPasswordNumbers] = useState(
		settingsDatastore.passwordNumbers,
	);
	const [passwordSpecialChars, setPasswordSpecialChars] = useState(
		settingsDatastore.passwordSpecialChars,
	);
	const [password, setPassword] = React.useState("");
	const [generated, setGenerated] = useState({ value: "", entropy: 0 });
	const onPassphraseChange = React.useCallback((value: string) => {
		if (value) browserClient.copyToClipboard(() => Promise.resolve(value));
		setPassword(value);
	}, []);
	const search = useSelector((state) => state.client.lastPopupSearch);
	const [items, setItems] = React.useState<PopupEntry[]>([]);
	let isSubscribed = true;
	const passwordFilter = helper.getPasswordFilter(search);
	const [url, setUrl] = React.useState<ParsedUrl>();
	const [changeAccountOpen, setChangeAccountOpen] = React.useState(false);
	const [datastores, setDatastores] = React.useState<DatastoreOverviewEntry[]>(
		[],
	);
	const [anchorDatastoreMenuEl, setAnchorDatastoreMenuEl] =
		React.useState<HTMLElement | null>(null);
	const [createDatastoreOpen, setCreateDatastoreOpen] = React.useState(false);

	useEffect(() => {
		if (offlineCacheService.isActive() && offlineCacheService.isLocked()) {
			setUnlockOfflineCache(true);
		} else {
			datastorePassword.getPasswordDatastore().then(onNewDatastoreLoaded);
		}
	}, [passwordDatastore]);

	const reloadDatastoreOverview = () => {
		datastoreService.getDatastoreOverview().then((overview) => {
			if (!isSubscribed) {
				return;
			}
			const newDatastores: DatastoreOverviewEntry[] = [];
			for (let i = 0; i < overview!.datastores.length; i++) {
				if (overview!.datastores[i]["type"] === "password") {
					newDatastores.push(overview!.datastores[i]);
				}
			}
			setDatastores(newDatastores);
		});
	};

	const onCreateDatastore = () => {
		setCreateDatastoreOpen(true);
		closeDatastoreMenu();
	};

	const onDatastoreSwitchClick = (datastore: DatastoreOverviewEntry) => {
		closeDatastoreMenu();
		datastoreService
			.saveDatastoreMeta(datastore.id, datastore.description, true)
			.then((result) => {
				reloadDatastoreOverview();
			});
	};

	const openDatastoreMenu = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorDatastoreMenuEl(event.currentTarget);
	};
	const closeDatastoreMenu = () => {
		setAnchorDatastoreMenuEl(null);
	};

	const openChangeAccount = () => {
		setChangeAccountOpen(true);
	};

	useHotkeys("alt+b", () => {
		// copy username
		if (itemsToDisplay.length > 0) {
			secretService.copyUsername(itemsToDisplay[0].content as SecretReference);
		}
	});

	useHotkeys("alt+c", () => {
		// copy password
		if (itemsToDisplay.length > 0) {
			secretService.copyPassword(itemsToDisplay[0].content as SecretReference);
		}
	});

	useHotkeys("alt+u", () => {
		// open url
		if (itemsToDisplay.length > 0) {
			secretService.onItemClick(itemsToDisplay[0].content as SecretReference);
		}
	});

	useHotkeys("alt+shift+u", () => {
		// copy url
		if (itemsToDisplay.length > 0) {
			secretService.copyUrl(itemsToDisplay[0].content as SecretReference);
		}
	});

	useHotkeys("alt+t", () => {
		// copy totp
		onCopyTotpToken();
	});

	const onCopyTotpToken = () => {
		if (itemsToDisplay.length > 0) {
			secretService.copyTotpToken(itemsToDisplay[0].content as SecretReference);
		}
	};

	const loadFromCache = async (): Promise<PopupEntry[] | null> => {
		try {
			// Get all password leafs from cache
			const leafs = await storage.where("datastore-password-leafs", () => true);

			if (!leafs || leafs.length === 0) {
				return null;
			}

			// Transform cache format to popup format
			const entries = leafs.map((leaf) => ({
				content: {
					secret_id: leaf.secret_id,
					secret_key: leaf.secret_key,
					name: leaf.name,
					description: leaf.description,
					urlfilter: leaf.urlfilter,
					type: leaf.type,
					autosubmit: leaf.autosubmit,
					allow_http: leaf.allow_http,
				},
				path: leaf.folder_path || "/",
			}));

			return entries;
		} catch (error) {
			console.error("Failed to load from cache:", error);
			return null;
		}
	};

	useEffect(() => {
		Promise.all([loadFromCache(), browserClient.getActiveTabUrl()]).then(
			([cachedEntries, activeUrl]) => {
				if (cachedEntries && cachedEntries.length > 0) {
					setItems(cachedEntries);
				}
				if (activeUrl) {
					setUrl(helper.parseUrl(activeUrl));
				}
			},
		);

		if (offlineCacheService.isActive() && offlineCacheService.isLocked()) {
			setUnlockOfflineCache(true);
		} else {
			datastorePassword.getPasswordDatastore().then(onNewDatastoreLoaded);
		}

		reloadDatastoreOverview();
		return () => {
			isSubscribed = false;
		};
	}, []);

	const onUnlockOfflineCacheClosed = () => {
		setUnlockOfflineCache(false);
		if (offlineCacheService.isActive() && offlineCacheService.isLocked()) {
			setUnlockOfflineCache(true);
		} else {
			datastorePassword.getPasswordDatastore().then(onNewDatastoreLoaded);
		}
	};

	const onNewDatastoreLoaded = (data: Datastore | undefined) => {
		if (!isSubscribed) {
			return;
		}
		const entries: PopupEntry[] = [];

		function deepSearchAllItems(folder: Datastore | undefined) {
			let i;
			if (!folder) {
				return;
			}
			if (Object.hasOwn(folder, "folders")) {
				for (i = 0; i < folder["folders"]!.length; i++) {
					if (
						Object.hasOwn(folder["folders"]![i], "deleted") &&
						folder["folders"]![i]["deleted"]
					) {
						continue;
					}
					deepSearchAllItems(folder["folders"]![i]);
				}
			}

			if (Object.hasOwn(folder, "items")) {
				for (i = 0; i < folder["items"]!.length; i++) {
					if (
						Object.hasOwn(folder["items"]![i], "deleted") &&
						folder["items"]![i]["deleted"]
					) {
						continue;
					}
					entries.push({
						content: folder["items"]![i],
						path: datastorePassword.getFolderPath(
							folder["items"]![i].path!,
							data!,
						),
					});
				}
			}
		}

		deepSearchAllItems(data);
		setItems(entries);
		accountService.broadcastReinitializeBackgroundEvent();
	};

	const logout = () => {
		browserClient.openTab("logout-success.html");
		browserClient.closePopup();
	};
	const back = () => {
		setView("default");
	};
	const openDatastore = () => {
		browserClient.openTab("index.html");
	};
	const copyToClipboard = (content: string) => {
		browserClient.copyToClipboard(() => Promise.resolve(content));
	};
	const generatePassword = (
		passwordLength: number | string,
		passwordLettersUppercase: string,
		passwordLettersLowercase: string,
		passwordNumbers: string,
		passwordSpecialChars: string,
	) => {
		const password = datastorePassword.generate(
			passwordLength,
			passwordLettersUppercase,
			passwordLettersLowercase,
			passwordNumbers,
			passwordSpecialChars,
		);
		setGenerated({
			value: password,
			entropy: passwordEntropy(
				password.length,
				passwordLettersUppercase +
					passwordLettersLowercase +
					passwordNumbers +
					passwordSpecialChars,
			),
		});
		copyToClipboard(password);
		setPassword(password);
	};
	const showGeneratePassword = () => {
		const defaultGenerator =
			settingsDatastore.defaultPasswordGenerator === "passphrase"
				? "passphrase"
				: "password";
		setGenerator(defaultGenerator);
		if (defaultGenerator === "password") {
			generatePassword(
				passwordLength,
				passwordLettersUppercase,
				passwordLettersLowercase,
				passwordNumbers,
				passwordSpecialChars,
			);
		} else {
			setPassword("");
		}
		setView("generate_password");
	};
	const saveGeneratePassword = () => {
		browserClient.emitSec("save-password-active-tab", { password: password });
		browserClient.emitSec("fillpassword-active-tab", { password: password });
		browserClient.closePopup();
	};
	const bookmark = () => {
		browserClient.emitSec("bookmark-active-tab", {});
		browserClient.closePopup();
	};
	const clear = () => {
		action().setLastPopupSearch("");
	};
	const editItem = (item: Datastore) => {
		browserClient.openTab(
			"index.html#!/datastore/edit/" + item.type + "/" + item.secret_id,
		);
	};
	const onItemClick = (item: Datastore) => {
		secretService.onItemClick(item as SecretReference);
	};

	function filterBySearch(item: PopupEntry) {
		if (!search) {
			// Hide all entries if we have not typed anything into the "search datastore..." field
			return false;
		}
		return passwordFilter(item.content, item.path);
	}

	if (isLoggedIn && user.requirePasswordChange()) {
		return (
			<DarkBox className={classes.popupContainer}>
				<Grid container>
					<Grid item xs={12} sm={12} md={12}>
						<MuiAlert
							severity="error"
							style={{
								marginBottom: "5px",
								marginTop: "5px",
							}}
						>
							{t("ADMINISTRATOR_REQUIRES_PASSWORD_CHANGE")}
						</MuiAlert>
					</Grid>
					<Grid item xs={12} sm={12} md={12}>
						<Button
							onClick={() => {
								browserClient.openTab("index.html#!/account/change-password");
							}}
							variant="contained"
							color="primary"
						>
							{t("CHANGE_PASSWORD")}
						</Button>
						<Button onClick={logout} variant="contained">
							{t("LOGOUT")}
						</Button>
					</Grid>
				</Grid>
			</DarkBox>
		);
	}

	if (isLoggedIn && !hasTwoFactor && user.requireTwoFaSetup()) {
		return (
			<DarkBox className={classes.popupContainer}>
				<Grid container>
					<Grid item xs={12} sm={12} md={12}>
						<MuiAlert
							severity="info"
							style={{
								marginBottom: "5px",
								marginTop: "5px",
							}}
						>
							{t("ADMINISTRATOR_REQUIRES_SECOND_FACTOR")}
						</MuiAlert>
					</Grid>
					<Grid item xs={12} sm={12} md={12}>
						<Button
							onClick={() => {
								browserClient.openTab("enforce-two-fa.html");
							}}
							variant="contained"
							color="primary"
						>
							{t("SETUP_SECOND_FACTOR")}
						</Button>
						<Button onClick={logout} variant="contained">
							{t("LOGOUT")}
						</Button>
					</Grid>
				</Grid>
			</DarkBox>
		);
	} else if (isLoggedIn && user.requireServerSecretModification()) {
		return (
			<DarkBox className={classes.popupContainer}>
				<Grid container>
					<Grid item xs={12} sm={12} md={12}>
						<MuiAlert
							severity="info"
							style={{
								marginBottom: "5px",
								marginTop: "5px",
							}}
						>
							{getStore().getState().user.serverSecretExists
								? t(
										"ADMINISTRATOR_REQUIRES_ACCOUNT_SWITCH_TO_CLIENT_SIDE_ENCRYPTION",
									)
								: t(
										"ADMINISTRATOR_REQUIRES_ACCOUNT_SWITCH_TO_SERVER_SIDE_ENCRYPTION",
									)}
						</MuiAlert>
					</Grid>
					<Grid item xs={12} sm={12} md={12}>
						<Button
							onClick={() => {
								browserClient.openTab("key-transfer.html");
							}}
							variant="contained"
							color="primary"
						>
							{t("CONFIGURE")}
						</Button>
						<Button onClick={logout} variant="contained">
							{t("LOGOUT")}
						</Button>
					</Grid>
				</Grid>
			</DarkBox>
		);
	}

	if (view === "generate_password") {
		return (
			<DarkBox className={classes.popupContainer}>
				<Grid container>
					<Grid item xs={12}>
						<GeneratorTypeSelect
							className={classes.textField}
							value={generator}
							onChange={(value) => {
								setGenerator(value);
								if (value === "password") {
									generatePassword(
										passwordLength,
										includeLettersUppercase ? passwordLettersUppercase : "",
										includeLettersLowercase ? passwordLettersLowercase : "",
										includeNumbers ? passwordNumbers : "",
										includeSpecialChars ? passwordSpecialChars : "",
									);
								} else {
									setPassword("");
								}
							}}
						/>
					</Grid>
					{generator === "passphrase" ? (
						<Grid item xs={12}>
							<PassphraseGenerator
								className={classes.textField}
								onChange={onPassphraseChange}
							/>
						</Grid>
					) : (
						<>
							<Grid item xs={12} sm={12} md={12}>
								<TextFieldColored
									className={classes.textField}
									variant="outlined"
									margin="dense"
									size="small"
									id="password"
									label={t("PASSWORD")}
									name="password"
									autoComplete="off"
									value={password}
									onChange={(event) => {
										setPassword(event.target.value);
										copyToClipboard(event.target.value);
									}}
									InputProps={{
										endAdornment: (
											<InputAdornment position="end">
												<IconButton
													aria-label="generate"
													onClick={() =>
														generatePassword(
															passwordLength,
															includeLettersUppercase
																? passwordLettersUppercase
																: "",
															includeLettersLowercase
																? passwordLettersLowercase
																: "",
															includeNumbers ? passwordNumbers : "",
															includeSpecialChars ? passwordSpecialChars : "",
														)
													}
													edge="end"
													className={classes.regularButtonText}
													size="large"
												>
													<ReplayRoundedIcon fontSize="small" />
												</IconButton>
											</InputAdornment>
										),
									}}
								/>
								<GeneratorStrength
									mode="password"
									entropy={
										password === generated.value ? generated.entropy : undefined
									}
								/>
							</Grid>
							<Grid item xs={12} sm={12} md={12}>
								<Divider classes={{ root: classes.divider }} />
								<TextField
									className={classes.textField}
									variant="outlined"
									margin="dense"
									size="small"
									id="passwordLength"
									label={t("PASSWORD_LENGTH")}
									name="passwordLength"
									autoComplete="off"
									value={passwordLength}
									onChange={(event) => {
										generatePassword(
											event.target.value,
											includeLettersUppercase ? passwordLettersUppercase : "",
											includeLettersLowercase ? passwordLettersLowercase : "",
											includeNumbers ? passwordNumbers : "",
											includeSpecialChars ? passwordSpecialChars : "",
										);
										setPasswordLength(event.target.value);
									}}
								/>
							</Grid>
							<Grid item xs={12} sm={12} md={12}>
								<Checkbox
									checked={includeLettersUppercase}
									onChange={(event) => {
										generatePassword(
											passwordLength,
											event.target.checked ? passwordLettersUppercase : "",
											includeLettersLowercase ? passwordLettersLowercase : "",
											includeNumbers ? passwordNumbers : "",
											includeSpecialChars ? passwordSpecialChars : "",
										);
										setIncludeLettersUppercase(event.target.checked);
									}}
									checkedIcon={<Check className={classes.checkedIcon} />}
									icon={<Check className={classes.uncheckedIcon} />}
									classes={{
										checked: classes.checked,
									}}
								/>{" "}
								{t("LETTERS_UPPERCASE")}
							</Grid>
							<Grid item xs={12} sm={12} md={12}>
								<Checkbox
									checked={includeLettersLowercase}
									onChange={(event) => {
										generatePassword(
											passwordLength,
											includeLettersUppercase ? passwordLettersUppercase : "",
											event.target.checked ? passwordLettersLowercase : "",
											includeNumbers ? passwordNumbers : "",
											includeSpecialChars ? passwordSpecialChars : "",
										);
										setIncludeLettersLowercase(event.target.checked);
									}}
									checkedIcon={<Check className={classes.checkedIcon} />}
									icon={<Check className={classes.uncheckedIcon} />}
									classes={{
										checked: classes.checked,
									}}
								/>{" "}
								{t("LETTERS_LOWERCASE")}
							</Grid>
							<Grid item xs={12} sm={12} md={12}>
								<Checkbox
									checked={includeNumbers}
									onChange={(event) => {
										generatePassword(
											passwordLength,
											includeLettersUppercase ? passwordLettersUppercase : "",
											includeLettersLowercase ? passwordLettersLowercase : "",
											event.target.checked ? passwordNumbers : "",
											includeSpecialChars ? passwordSpecialChars : "",
										);
										setIncludeNumbers(event.target.checked);
									}}
									checkedIcon={<Check className={classes.checkedIcon} />}
									icon={<Check className={classes.uncheckedIcon} />}
									classes={{
										checked: classes.checked,
									}}
								/>{" "}
								{t("NUMBERS")}
							</Grid>
							<Grid item xs={12} sm={12} md={12}>
								<Checkbox
									checked={includeSpecialChars}
									onChange={(event) => {
										generatePassword(
											passwordLength,
											includeLettersUppercase ? passwordLettersUppercase : "",
											includeLettersLowercase ? passwordLettersLowercase : "",
											includeNumbers ? passwordNumbers : "",
											event.target.checked ? passwordSpecialChars : "",
										);
										setIncludeSpecialChars(event.target.checked);
									}}
									checkedIcon={<Check className={classes.checkedIcon} />}
									icon={<Check className={classes.uncheckedIcon} />}
									classes={{
										checked: classes.checked,
									}}
								/>{" "}
								{t("SPECIAL_CHARS")}
							</Grid>
						</>
					)}
					<Grid item xs={12} sm={12} md={12}>
						<Divider classes={{ root: classes.divider }} />
						<Button
							variant="contained"
							color="primary"
							onClick={saveGeneratePassword}
							disabled={!password}
						>
							{t("SAVE")}
						</Button>
						<Button className={classes.regularButtonText} onClick={back}>
							{t("BACK")}
						</Button>
					</Grid>
				</Grid>
			</DarkBox>
		);
	}

	const sortByName = (a: PopupEntry, b: PopupEntry) => {
		const nameA = (a.content.name || "").toLowerCase();
		const nameB = (b.content.name || "").toLowerCase();
		const nameCompare = nameA.localeCompare(nameB);
		if (nameCompare !== 0) {
			return nameCompare;
		}
		const descA = (a.content.description || "").toLowerCase();
		const descB = (b.content.description || "").toLowerCase();
		return descA.localeCompare(descB);
	};

	let itemsToDisplay: PopupEntry[] = [];
	if (search) {
		itemsToDisplay = items.filter(filterBySearch).sort(sortByName).slice(0, 50);
	} else if (url) {
		const matching: PopupEntry[] = [];
		const notMatching: PopupEntry[] = [];
		items.map((item, _) => {
			if (Object.hasOwn(item.content, "deleted") && item.content["deleted"]) {
				return;
			}
			if (
				item.content.urlfilter &&
				item.content.urlfilter
					.split(/\s+|,|;/)
					.some((filter) => helper.isUrlFilterMatch(url.authority, filter))
			) {
				matching.push(item);
			} else {
				notMatching.push(item);
			}
		});
		matching.sort(sortByName);
		notMatching.sort(sortByName);
		itemsToDisplay = matching.concat(notMatching).slice(0, 50);
	} else {
		itemsToDisplay = items.sort(sortByName).slice(0, 50);
	}

	return (
		<DarkBox className={classes.popupContainer}>
			<Grid container>
				<Grid item xs={12} sm={12} md={12}>
					<TextField
						className={classes.textField}
						variant="outlined"
						margin="dense"
						size="small"
						id="search"
						label={t("SEARCH_DATASTORE")}
						name="search"
						autoComplete="off"
						autoFocus
						onFocus={(event) => {
							event.target.select();
						}}
						value={search}
						onChange={(event) => {
							action().setLastPopupSearch(event.target.value);
						}}
						InputProps={{
							endAdornment: search && (
								<InputAdornment position="end">
									<IconButton
										aria-label="clear search"
										onClick={clear}
										edge="end"
										className={classes.regularButtonText}
										size="large"
									>
										<ClearIcon fontSize="small" />
									</IconButton>
								</InputAdornment>
							),
						}}
					/>
					<Divider classes={{ root: classes.divider }} />
				</Grid>
				<Grid item xs={12} sm={12} md={12}>
					<div className={classes.entriesContainer}>
						{itemsToDisplay.length > 0 ? (
							<ul className={classes.navigation}>
								{itemsToDisplay.map((item) => (
									<PopupItem
										key={item.content.secret_id || item.content.file_id}
										editItem={editItem}
										onItemClick={onItemClick}
										item={item}
									/>
								))}
							</ul>
						) : (
							<div
								className={`${classes.noEntriesMessage} ${classes.regularButtonText}`}
							>
								{t("NO_ENTRY_FOUND")}
							</div>
						)}
					</div>
				</Grid>
				<Grid item xs={12} sm={12} md={12}>
					<Divider classes={{ root: classes.divider }} />
					<Grid container spacing={1}>
						<Grid item>
							<ButtonGroup
								variant="contained"
								color="primary"
								disableElevation
								aria-label="datastore menu"
							>
								<Tooltip
									disableInteractive
									title={t("OPEN_DATASTORE")}
									placement="top"
								>
									<Button
										onClick={openDatastore}
										className={classes.menuButton}
									>
										<StorageRoundedIcon />
									</Button>
								</Tooltip>
								{datastores && datastores.length > 1 && (
									<Tooltip
										disableInteractive
										title={t("CHANGE_DATASTORE")}
										placement="top"
									>
										<Button
											onClick={openDatastoreMenu}
											className={`${classes.menuButton} ${classes.menuButtonSlim}`}
										>
											<KeyboardArrowUpIcon />
										</Button>
									</Tooltip>
								)}
							</ButtonGroup>
							<Menu
								id="datastore-menu"
								anchorEl={anchorDatastoreMenuEl}
								keepMounted
								open={Boolean(anchorDatastoreMenuEl)}
								onClose={closeDatastoreMenu}
								anchorOrigin={{
									vertical: "top",
									horizontal: "right",
								}}
								transformOrigin={{
									vertical: "bottom",
									horizontal: "right",
								}}
							>
								{datastores.map((datastore, index) => {
									return (
										<MenuItem
											onClick={() => {
												onDatastoreSwitchClick(datastore);
											}}
											key={index}
										>
											<ListItemIcon className={classes.listItemIcon}>
												{datastore.is_default ? (
													<CheckBoxIcon className={classes.icon} />
												) : (
													<CheckBoxOutlineBlankIcon className={classes.icon} />
												)}
											</ListItemIcon>
											<Typography variant="body2">
												{datastore.description}
											</Typography>
										</MenuItem>
									);
								})}
								{!offlineCache.isActive() && [
									<Divider key={"divider"} />,
									<MenuItem
										onClick={onCreateDatastore}
										key={"create-datastore"}
									>
										<ListItemIcon className={classes.listItemIcon}>
											<AddIcon className={classes.icon} />
										</ListItemIcon>
										<Typography variant="body2">
											{t("CREATE_NEW_DATASTORE")}
										</Typography>
									</MenuItem>,
								]}
							</Menu>
						</Grid>
						{settingsDatastore.showBookmark && (
							<Grid item>
								<Tooltip
									disableInteractive
									title={t("BOOKMARK")}
									placement="top"
								>
									<Button
										variant="outlined"
										color="primary"
										onClick={bookmark}
										className={classes.menuButton}
									>
										<BookmarkBorderRoundedIcon color="primary" />
									</Button>
								</Tooltip>
							</Grid>
						)}
						<Grid item>
							<Tooltip
								disableInteractive
								title={t("GENERATE_PASSWORD")}
								placement="top"
							>
								<Button
									variant="outlined"
									color="primary"
									onClick={showGeneratePassword}
									className={classes.menuButton}
								>
									<VpnKeyRoundedIcon color="primary" />
								</Button>
							</Tooltip>
						</Grid>
						<Grid item style={{ marginLeft: "auto" }}>
							<ButtonGroup
								variant="outlined"
								color="primary"
								disableElevation
								aria-label="main menu"
							>
								<Tooltip
									disableInteractive
									title={t("CHANGE_ACCOUNT")}
									placement="top"
								>
									<Button
										onClick={openChangeAccount}
										className={classes.menuButton}
									>
										<Badge
											overlap="circular"
											anchorOrigin={{
												vertical: "bottom",
												horizontal: "right",
											}}
											badgeContent={
												<SettingsIcon className={classes.overlayIcon} />
											}
										>
											<Avatar className={classes.overlayedIcon}>
												<SupervisorAccountIcon />
											</Avatar>
										</Badge>
									</Button>
								</Tooltip>
								<Tooltip disableInteractive title={t("LOGOUT")} placement="top">
									<Button onClick={logout} className={classes.menuButton}>
										<ExitToAppRoundedIcon color="primary" />
									</Button>
								</Tooltip>
							</ButtonGroup>
						</Grid>
					</Grid>
				</Grid>
				{changeAccountOpen && (
					<DialogChangeAccount
						open={changeAccountOpen}
						onClose={() => setChangeAccountOpen(false)}
					/>
				)}
				{unlockOfflineCache && (
					<DialogUnlockOfflineCache
						open={unlockOfflineCache}
						onClose={onUnlockOfflineCacheClosed}
					/>
				)}
				{createDatastoreOpen && (
					<CreateDatastoresDialog
						{...props}
						open={createDatastoreOpen}
						onClose={() => {
							setCreateDatastoreOpen(false);
							reloadDatastoreOverview();
						}}
					/>
				)}
			</Grid>
		</DarkBox>
	);
};

export default PopupView;
